import { useCallback, useEffect, useRef, useState } from "react";
import type { SubmissionMedia } from "@coaching/types";
import { api, ApiError } from "../api";

export type MediaState =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; media: SubmissionMedia; generation: number }
    | { kind: "error"; message: string; retryable: boolean };

const REFRESH_LEAD_MS = 60_000;

/**
 * The signed playlists of one submission. One signature covers every clip
 * for `signedUrlTtlSec`; this fetches again a minute before it passes and
 * on demand when the player reports a failure mid-playback, which is what
 * an expired signature looks like. Each refresh bumps `generation`; the
 * stage uses that to reload the same clip where it was.
 */
export function useMedia(submissionId: string, clipsReady: boolean) {
    const [state, setState] = useState<MediaState>({ kind: "idle" });
    const generation = useRef(0);
    const timer = useRef<number | null>(null);
    const inFlight = useRef<Promise<void> | null>(null);

    const load = useCallback(async (silent: boolean) => {
        if (inFlight.current) return inFlight.current;
        if (!silent) setState({ kind: "loading" });
        const p = (async () => {
            try {
                const media = await api.media(submissionId);
                generation.current += 1;
                setState({ kind: "ready", media, generation: generation.current });
                if (timer.current) window.clearTimeout(timer.current);
                const wait = Math.max(5_000, media.expiresAt * 1000 - Date.now() - REFRESH_LEAD_MS);
                timer.current = window.setTimeout(() => void load(true), wait);
            } catch (err) {
                const e = err instanceof ApiError ? err : null;
                const message = e?.code === "failed-precondition" ? e.message
                    : e?.code === "not-found" ? "This submission is no longer available."
                        : "Could not load the clips. Check your connection and try again.";
                setState({ kind: "error", message, retryable: e?.code !== "not-found" });
            } finally {
                inFlight.current = null;
            }
        })();
        inFlight.current = p;
        return p;
    }, [submissionId]);

    useEffect(() => {
        if (!clipsReady) { setState({ kind: "idle" }); return; }
        void load(false);
        return () => { if (timer.current) window.clearTimeout(timer.current); };
    }, [clipsReady, load]);

    return { state, refresh: () => load(true), retry: () => load(false) };
}
